import type { PostgrestError } from '@supabase/supabase-js';
import { JPH_APP_BUILD_LABEL } from '@/lib/appBuild';

export { JPH_APP_BUILD_LABEL };

export type DailyLogShareOperation =
  | 'load_daily_log'
  | 'load_photos'
  | 'save_pm_client_comment'
  | 'upload_pm_photo'
  | 'link_pm_photo'
  | 'save_draft'
  | 'validate_selected_photos'
  | 'update_selected_photos'
  | 'share_with_client'
  | 'update_client_visibility'
  | 'refresh_after_save'
  | 'delete_pm_photo';

export interface DailyLogShareError {
  operation: DailyLogShareOperation;
  message: string;
  details?: string;
  hint?: string;
  code?: string;
  dailyLogId?: string;
  projectId?: string;
  logDate?: string;
  photoId?: string;
  fileName?: string;
  recoveryHint?: string;
}

export interface PhotoShareValidation {
  id: string;
  valid: boolean;
  reason?: string;
  fileName?: string;
}

export const PM_PHOTO_MAX_BYTES = 15 * 1024 * 1024;
export const PM_PHOTO_SUPPORTED_MIME = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);
export const PM_PHOTO_SUPPORTED_EXT = new Set(['jpg', 'jpeg', 'png', 'webp']);

export function fromSupabaseError(
  operation: DailyLogShareOperation,
  error: PostgrestError | { message: string; details?: string; hint?: string; code?: string },
  context?: Partial<DailyLogShareError>
): DailyLogShareError {
  return {
    operation,
    message: error.message,
    details: error.details ?? undefined,
    hint: error.hint ?? undefined,
    code: 'code' in error ? error.code : undefined,
    recoveryHint: isStaleStateError(error.message)
      ? 'Please refresh the page and try again.'
      : undefined,
    ...context,
  };
}

export function shareError(
  operation: DailyLogShareOperation,
  message: string,
  context?: Partial<DailyLogShareError>
): DailyLogShareError {
  return { operation, message, ...context };
}

export function formatDailyLogShareError(err: DailyLogShareError): string {
  const lines = [`[${formatOperationLabel(err.operation)}] ${err.message}`];
  if (err.details) lines.push(`Details: ${err.details}`);
  if (err.hint) lines.push(`Hint: ${err.hint}`);
  if (err.code) lines.push(`Code: ${err.code}`);
  if (err.dailyLogId) lines.push(`daily_log_id: ${err.dailyLogId}`);
  if (err.photoId) lines.push(`photo_id: ${err.photoId}`);
  if (err.fileName) lines.push(`file: ${err.fileName}`);
  if (err.recoveryHint) lines.push(`Recovery: ${err.recoveryHint}`);
  lines.push(`build: ${JPH_APP_BUILD_LABEL}`);
  return lines.join('\n');
}

export function formatOperationLabel(operation: DailyLogShareOperation): string {
  return operation.replace(/_/g, ' ');
}

export async function copyDailyLogShareError(err: DailyLogShareError): Promise<boolean> {
  const text = formatDailyLogShareError(err);
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function validatePhotoRecord(row: {
  id?: string | null;
  file_url?: string | null;
  file_name?: string | null;
  is_deleted?: boolean | null;
  storage_path?: string | null;
}): PhotoShareValidation {
  const id = typeof row.id === 'string' ? row.id : '';
  const fileName = typeof row.file_name === 'string' ? row.file_name : undefined;
  if (!id) {
    return { id: '', valid: false, reason: 'Missing photo id', fileName };
  }
  if (row.is_deleted) {
    return {
      id,
      valid: false,
      reason: 'This photo cannot be shared because its file record was deleted.',
      fileName,
    };
  }
  const url = typeof row.file_url === 'string' ? row.file_url.trim() : '';
  if (!url) {
    return {
      id,
      valid: false,
      reason: 'This photo cannot be shared because its file record is incomplete (missing file URL).',
      fileName,
    };
  }
  return { id, valid: true, fileName };
}

export function validatePmUploadFile(file: File): { ok: true } | { ok: false; error: DailyLogShareError } {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const mime = (file.type || '').toLowerCase();

  if (
    ext === 'heic' ||
    ext === 'heif' ||
    mime === 'image/heic' ||
    mime === 'image/heif'
  ) {
    return {
      ok: false,
      error: shareError('upload_pm_photo', 'HEIC/HEIF is not supported. Please convert to JPG or PNG.', {
        fileName: file.name,
      }),
    };
  }

  if (mime.startsWith('video/') || ext === 'pdf' || mime === 'application/pdf') {
    return {
      ok: false,
      error: shareError('upload_pm_photo', 'Only JPG, PNG, and WEBP images are supported.', {
        fileName: file.name,
      }),
    };
  }

  const mimeOk = !mime || PM_PHOTO_SUPPORTED_MIME.has(mime);
  const extOk = PM_PHOTO_SUPPORTED_EXT.has(ext);
  if (!mimeOk && !extOk) {
    return {
      ok: false,
      error: shareError('upload_pm_photo', 'Only JPG, PNG, and WEBP images are supported.', {
        fileName: file.name,
      }),
    };
  }

  if (file.size > PM_PHOTO_MAX_BYTES) {
    return {
      ok: false,
      error: shareError('upload_pm_photo', 'File exceeds the 15 MB upload limit.', {
        fileName: file.name,
      }),
    };
  }

  return { ok: true };
}

function isStaleStateError(message: string): boolean {
  return /stale|schema cache|jwt|session|network|fetch failed|load failed/i.test(message);
}

export interface DailyLogShareDebugSnapshot {
  dailyLogId: string | null;
  projectId: string;
  logDate: string;
  clientVersionStatus: string | null;
  clientSharedAt: string | null;
  selectedSubcontractorPhotoIds: string[];
  pmAddedPhotoIds: string[];
  validPhotoCount: number;
  invalidPhotoCount: number;
  invalidPhotoReasons: Array<{ id: string; reason: string }>;
  build: string;
}

export function logDailyLogShareDebug(label: string, snapshot: DailyLogShareDebugSnapshot): void {
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console
  console.groupCollapsed(`[DailyLogShare] ${label}`);
  // eslint-disable-next-line no-console
  console.table(snapshot);
  // eslint-disable-next-line no-console
  console.groupEnd();
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Normalize a daily log id for PostgREST filters.
 * Never returns the literal strings "undefined" / "null".
 * Never converts undefined → String("undefined").
 */
export function normalizeDailyLogId(id: unknown): string | null {
  if (typeof id !== 'string') return null;
  const normalized = id.trim();
  if (!normalized) return null;
  const lower = normalized.toLowerCase();
  if (lower === 'undefined' || lower === 'null') return null;
  if (!UUID_RE.test(normalized)) return null;
  return normalized;
}

/** Reject null, empty, literal "undefined"/"null", and non-UUID values. */
export function isValidDailyLogId(id: unknown): id is string {
  return normalizeDailyLogId(id) !== null;
}

export function logShareQueryDebug(
  operation: string,
  ctx: { dailyLogId?: string | null; projectId?: string | null; logDate?: string | null }
): void {
  // eslint-disable-next-line no-console
  console.debug(`[DailyLogShare] query ${operation}`, {
    dailyLogId: ctx.dailyLogId ?? null,
    projectId: ctx.projectId ?? null,
    logDate: ctx.logDate ?? null,
  });
}
